import { useState } from 'react'
import { AlertTriangle, BarChart3, Search, Zap, Shield, CheckCircle2, Clock, Play } from 'lucide-react'

export default function VulnerabilityAssessmentPage() {
  const [stats] = useState({
    vulnerabilitiesScanned: 8947,
    criticalVulns: 12,
    highVulns: 34,
    mediumVulns: 127,
  })

  const scanTypes = [
    {
      name: 'Network Vulnerability Scan',
      description: 'Automated scanning for network vulnerabilities and misconfigurations',
      targets: ['Servers', 'Routers', 'Firewalls', 'Load Balancers', 'IDS/IPS Systems'],
      frequency: 'Weekly',
      color: 'from-red-600 to-red-500',
      icon: AlertTriangle,
    },
    {
      name: 'Web Application Vulnerability Scan',
      description: 'DAST & SAST scanning for web app vulnerabilities',
      targets: ['Web Apps', 'APIs', 'Cloud Apps', 'Microservices', 'Serverless Functions'],
      frequency: 'Daily',
      color: 'from-orange-600 to-orange-500',
      icon: Search,
    },
    {
      name: 'Database Vulnerability Scan',
      description: 'Automated scanning for database security issues',
      targets: ['SQL Databases', 'NoSQL Databases', 'Data Warehouses', 'Cache Systems', 'Backups'],
      frequency: 'Weekly',
      color: 'from-yellow-600 to-yellow-500',
      icon: Zap,
    },
    {
      name: 'Code Vulnerability Scan (SAST)',
      description: 'Static Application Security Testing in source code',
      targets: ['GitHub Repos', 'GitLab', 'Bitbucket', 'Source Code', 'Dependencies'],
      frequency: 'On Commit',
      color: 'from-purple-600 to-purple-500',
      icon: Shield,
    },
    {
      name: 'Dependency & Library Scan',
      description: 'Scan for vulnerable third-party libraries and dependencies',
      targets: ['NPM Packages', 'Maven Dependencies', 'PyPI Packages', 'RubyGems', 'NuGet Packages'],
      frequency: 'Continuous',
      color: 'from-blue-600 to-blue-500',
      icon: CheckCircle2,
    },
    {
      name: 'Container & Image Scan',
      description: 'Scan Docker images and Kubernetes containers for vulnerabilities',
      targets: ['Docker Images', 'Kubernetes Pods', 'Container Registry', 'Container Config', 'Runtime Security'],
      frequency: 'On Build',
      color: 'from-green-600 to-green-500',
      icon: Zap,
    },
  ]

  const vulnerabilityCategories = [
    { name: 'Critical', count: stats.criticalVulns, color: 'bg-red-600', severity: '9.0-10.0 CVSS' },
    { name: 'High', count: stats.highVulns, color: 'bg-orange-600', severity: '7.0-8.9 CVSS' },
    { name: 'Medium', count: stats.mediumVulns, color: 'bg-yellow-600', severity: '4.0-6.9 CVSS' },
    { name: 'Low', count: 89, color: 'bg-blue-600', severity: '0.1-3.9 CVSS' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-red-600 via-pink-600 to-purple-600 p-8 text-white shadow-2xl border border-red-500/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <AlertTriangle className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Vulnerability Assessment</h1>
                </div>
                <p className="text-red-100 text-lg font-semibold">AI-Powered Vulnerability Detection & Management</p>
                <p className="text-red-200 text-sm mt-1">Network • Web Apps • Database • Code • Dependencies • Containers</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.vulnerabilitiesScanned}</div>
                <div className="text-sm text-red-100">Vulnerabilities Scanned</div>
              </div>
            </div>
          </div>
        </div>

        {/* Severity Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {vulnerabilityCategories.map((cat, idx) => (
            <div key={idx} className={`${cat.color} bg-gradient-to-br rounded-xl p-6 text-white shadow-lg`}>
              <p className="text-sm font-semibold opacity-90">{cat.name} Severity</p>
              <p className="text-4xl font-black mt-2">{cat.count}</p>
              <p className="text-xs mt-2 opacity-75">{cat.severity}</p>
            </div>
          ))}
        </div>

        {/* Scan Types */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6">Vulnerability Scan Types</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {scanTypes.map((scan, idx) => {
              const Icon = scan.icon
              return (
                <div key={idx} className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                  <div className={`h-1 w-full bg-gradient-to-r ${scan.color} rounded-full mb-4 shadow-lg`}></div>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-white text-lg">{scan.name}</h3>
                      <p className="text-blue-300 text-sm">{scan.description}</p>
                    </div>
                    <div className={`bg-gradient-to-br ${scan.color} p-3 rounded-lg`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="mb-4">
                    <p className="text-cyan-400 text-xs font-bold mb-2">Scan Targets:</p>
                    <div className="flex flex-wrap gap-2">
                      {scan.targets.map((target, tidx) => (
                        <span key={tidx} className="text-xs bg-slate-700/50 text-blue-300 px-2 py-1 rounded border border-blue-500/20">
                          {target}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-blue-500/20">
                    <span className="text-cyan-400 text-sm font-semibold">Frequency: {scan.frequency}</span>
                    <button className={`bg-gradient-to-r ${scan.color} hover:shadow-lg transition-all px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center space-x-2`}>
                      <Play className="w-4 h-4" />
                      <span>Scan Now</span>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Vulnerability Database */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-2xl font-bold text-white mb-6">Vulnerability Intelligence</h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="p-4 bg-slate-700/50 rounded-lg border border-blue-500/20">
              <h4 className="text-blue-300 font-bold mb-3">CVE Database</h4>
              <p className="text-slate-300 text-sm mb-4">Connected to NVD (National Vulnerability Database) with 200,000+ CVEs</p>
              <ul className="space-y-2 text-blue-300 text-sm">
                <li>✓ Real-time CVE updates</li>
                <li>✓ CVSS scoring</li>
                <li>✓ Exploit availability tracking</li>
                <li>✓ Patch availability alerts</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-700/50 rounded-lg border border-blue-500/20">
              <h4 className="text-blue-300 font-bold mb-3">Common Weakness</h4>
              <p className="text-slate-300 text-sm mb-4">CWE/OWASP Top 10 vulnerability mapping</p>
              <ul className="space-y-2 text-blue-300 text-sm">
                <li>✓ CWE categorization</li>
                <li>✓ OWASP mapping</li>
                <li>✓ SANS Top 25</li>
                <li>✓ Trend analysis</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-700/50 rounded-lg border border-blue-500/20">
              <h4 className="text-blue-300 font-bold mb-3">Exploit Intelligence</h4>
              <p className="text-slate-300 text-sm mb-4">Active exploitation tracking and risk assessment</p>
              <ul className="space-y-2 text-blue-300 text-sm">
                <li>✓ Exploit availability</li>
                <li>✓ Active campaigns</li>
                <li>✓ Proof of concept code</li>
                <li>✓ Weaponization status</li>
              </ul>
            </div>
          </div>
        </div>

        {/* AI-Powered Scanning Tools */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6">AI-Powered Scanning Engines</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                name: 'Checkmarx SAST',
                type: 'Static Code Analysis',
                features: ['Code vulnerability detection', 'Line-of-code fixes', 'CWE/OWASP mapping', 'Language support: 25+'],
                color: 'from-blue-600 to-blue-500'
              },
              {
                name: 'SentinelOne Cloud Auditor',
                type: 'Runtime Security',
                features: ['Container scanning', 'API security', 'Configuration audits', 'Cloud workload monitoring'],
                color: 'from-purple-600 to-purple-500'
              },
              {
                name: 'Burp Suite Professional',
                type: 'Web Application Scanner',
                features: ['Dynamic web app scanning', 'AI vulnerability prioritization', 'Crawling & auditing', 'Exploit chain detection'],
                color: 'from-orange-600 to-orange-500'
              },
              {
                name: 'Nessus Professional',
                type: 'Network Vulnerability Scanner',
                features: ['Credentialed scanning', 'Compliance scanning', 'Patch auditing', '100,000+ plugins'],
                color: 'from-red-600 to-red-500'
              },
              {
                name: 'Qualys VMDR',
                type: 'Cloud Vulnerability Management',
                features: ['Continuous scanning', 'Remediation tracking', 'Threat correlation', 'Risk scoring'],
                color: 'from-green-600 to-green-500'
              },
              {
                name: 'Trivy + Grype',
                type: 'Container & Dependency Scanning',
                features: ['Container image scanning', 'SBOM generation', 'Dependency tracking', 'CI/CD integration'],
                color: 'from-cyan-600 to-cyan-500'
              },
            ].map((tool, idx) => (
              <div key={idx} className={`bg-gradient-to-br ${tool.color} rounded-xl p-4 text-white border border-opacity-20`}>
                <h4 className="font-bold text-lg mb-1">{tool.name}</h4>
                <p className="text-sm opacity-90 mb-3">{tool.type}</p>
                <ul className="space-y-1 text-xs opacity-90">
                  {tool.features.map((feat, fidx) => (
                    <li key={fidx}>• {feat}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Vulnerability Management Lifecycle */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-2xl font-bold text-white mb-6">Vulnerability Management Lifecycle</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { step: '1. Discover', icon: '🔍', desc: 'Automated scanning identifies vulnerabilities' },
              { step: '2. Analyze', icon: '📊', desc: 'AI prioritizes by risk and exploitability' },
              { step: '3. Remediate', icon: '🔧', desc: 'Developers fix with guided remediation' },
              { step: '4. Verify', icon: '✓', desc: 'Re-scanning confirms fixes' },
            ].map((item, idx) => (
              <div key={idx} className="p-4 bg-slate-700/50 rounded-lg border border-blue-500/20 text-center">
                <div className="text-3xl mb-2">{item.icon}</div>
                <p className="text-blue-300 font-bold mb-1">{item.step}</p>
                <p className="text-blue-300 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance Scanning */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-xl font-bold text-white mb-4">Compliance Vulnerability Scanning</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              'PCI-DSS', 'HIPAA', 'SOC 2', 'ISO 27001',
              'NIST 800-53', 'CIS Controls', 'GDPR', 'COBIT'
            ].map((comp, idx) => (
              <div key={idx} className="p-3 bg-slate-700/50 rounded-lg border border-green-500/20 text-center">
                <p className="text-green-300 text-sm font-bold">{comp}</p>
                <p className="text-green-400 text-xs mt-1">✓ Scanning Enabled</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
